import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useCurrentUser } from "../auth";

type StoredDraft = { value: unknown; version: string; storageError: boolean };
// Survives navigation when browser storage is blocked. Null records an explicit
// removal so a failed disk removal cannot recover discarded wording in-session.
const sessionDrafts = new Map<string, StoredDraft | null>();

/** Browser-local recovery only. No network request or authentication data is stored. */
export function useLocalDraft<T>(
  identity: string,
  initial: T,
  version: string,
) {
  const { currentUser, impersonatedOrgId } = useCurrentUser();
  const key = `valid-labels:draft:v1:${currentUser?.sub}:${impersonatedOrgId || currentUser?.org_id}:${identity}`;
  const initialJson = JSON.stringify(initial);
  type State = {
    key: string;
    value: T;
    base: string;
    version: string;
    recovered: boolean;
    stale: boolean;
    storageError: boolean;
  };
  const read = (): State => {
    const next: State = {
      key,
      value: initial,
      base: initialJson,
      version,
      recovered: false,
      stale: false,
      storageError: false,
    };
    let saved: StoredDraft | null | undefined;
    if (sessionDrafts.has(key)) saved = sessionDrafts.get(key);
    else {
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const candidate = JSON.parse(raw);
          if (
            !candidate ||
            typeof candidate !== "object" ||
            !("value" in candidate) ||
            typeof candidate.version !== "string"
          )
            throw new Error("Invalid local draft");
          saved = { ...candidate, storageError: false };
        }
      } catch {
        next.storageError = true;
      }
    }
    if (saved) {
      next.value = saved.value as T;
      next.version = saved.version;
      next.recovered = true;
      next.stale = saved.version !== version;
      next.storageError = saved.storageError;
    }
    return next;
  };
  const [state, setState] = useState<State>(read);
  const ref = useRef(state);
  ref.current = state;
  const activeKey = useRef(key);
  activeKey.current = key;
  useLayoutEffect(() => {
    const previous = ref.current;
    let next: State;
    if (previous.key !== key) next = read();
    else if (JSON.stringify(previous.value) !== previous.base) {
      // A later server read warns about an old draft, never replaces it.
      next = {
        ...previous,
        base: initialJson,
        stale: previous.version !== version,
      };
    } else {
      // A clean editor follows server refreshes while the component stays mounted.
      next = {
        key,
        value: initial,
        base: initialJson,
        version,
        recovered: false,
        stale: false,
        storageError: previous.storageError,
      };
    }
    ref.current = next;
    setState(next);
  }, [key, version, initialJson]);
  const persist = (next: State) => {
    const saved =
      JSON.stringify(next.value) !== next.base
        ? { value: next.value, version: next.version, storageError: false }
        : null;
    try {
      if (saved)
        localStorage.setItem(
          next.key,
          JSON.stringify({ value: saved.value, version: saved.version }),
        );
      else localStorage.removeItem(next.key);
      next.storageError = false;
    } catch {
      next.storageError = true;
    }
    sessionDrafts.set(
      next.key,
      saved ? { ...saved, storageError: next.storageError } : null,
    );
    // A response captured in another workspace cannot replace the active draft.
    if (activeKey.current === next.key) {
      ref.current = next;
      setState(next);
    }
  };
  const setValue: Dispatch<SetStateAction<T>> = (update) => {
    const current = ref.current.key === key ? ref.current : read();
    const value =
      typeof update === "function"
        ? (update as (old: T) => T)(current.value)
        : update;
    const matchesSaved = JSON.stringify(value) === initialJson;
    persist(
      matchesSaved
        ? {
            ...current,
            value,
            base: initialJson,
            version,
            recovered: false,
            stale: false,
          }
        : { ...current, value },
    );
  };
  // Accept a server save while retaining edits outside that save's scope.
  const rebase = (base: T, nextVersion: string, value: T) =>
    persist({
      key,
      value,
      base: JSON.stringify(base),
      version: nextVersion,
      recovered: false,
      stale: false,
      storageError: false,
    });
  const reset = (value: T, nextVersion = version) =>
    rebase(value, nextVersion, value);
  const current =
    state.key === key
      ? state
      : {
          key,
          value: initial,
          base: initialJson,
          version,
          recovered: false,
          stale: false,
          storageError: false,
        };
  const dirty = JSON.stringify(current.value) !== current.base;
  useEffect(() => {
    if (!dirty || !current.storageError) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty, current.storageError]);
  return {
    value: current.value,
    setValue,
    reset,
    rebase,
    dirty,
    recovered: current.recovered,
    stale: current.stale,
    storageError: current.storageError,
  };
}
